import { useState } from "react";
import { Camera, Hash, Calendar, Heart, FileText, Edit, Share2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const speciesLabels = {
  dog: "Cane",
  cat: "Gatto",
  bird: "Uccello",
  rabbit: "Coniglio",
  horse: "Cavallo",
  other: "Altro"
};

const formatBreed = (breed) => {
  if (!breed) return null;
  return breed
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function PetProfileHeader({ 
  pet, 
  onBack, 
  onEdit, 
  onShare,
  compact = false 
}) {
  if (!pet) return null;
  
  const photoUrl = pet.photo_url;
  
  // Compact header for scrolled state
  if (compact) {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        <button 
          onClick={onBack}
          className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center"
          data-testid="back-button-compact"
        >
          <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
        </button>
        
        <div className="w-10 h-10 rounded-full overflow-hidden bg-[#F5F5F7] dark:bg-[#2C2C2E] flex-shrink-0">
          {photoUrl ? (
            <img src={photoUrl} alt={pet.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-lg">🐾</span>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-[#1D1D1F] dark:text-white truncate">
            {pet.name}
          </h1>
        </div>
        
        <button 
          onClick={onShare}
          className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center"
          data-testid="share-button-compact"
        >
          <Share2 size={18} className="text-[#1D1D1F] dark:text-white" />
        </button>
      </div>
    );
  }
  
  // Full header
  return (
    <div className="px-4 pt-4 pb-6">
      {/* Navigation Row */}
      <div className="flex items-center justify-between mb-6">
        <button 
          onClick={onBack}
          className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center transition-colors hover:bg-[#EBEBEB] dark:hover:bg-[#3C3C3E]"
          data-testid="back-button"
        >
          <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
        </button>
        
        <div className="flex gap-2">
          <button 
            onClick={onShare}
            className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center transition-colors hover:bg-[#EBEBEB] dark:hover:bg-[#3C3C3E]"
            data-testid="share-button"
          >
            <Share2 size={18} className="text-[#1D1D1F] dark:text-white" />
          </button>
          <button 
            onClick={onEdit}
            className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center transition-colors hover:bg-[#EBEBEB] dark:hover:bg-[#3C3C3E]"
            data-testid="edit-button"
          >
            <Edit size={18} className="text-[#1D1D1F] dark:text-white" />
          </button>
        </div>
      </div>
      
      {/* Pet Identity */}
      <div className="flex items-start gap-5">
        {/* Photo */}
        <div 
          className="w-24 h-24 rounded-3xl overflow-hidden bg-[#F5F5F7] dark:bg-[#2C2C2E] flex-shrink-0 shadow-sm cursor-pointer"
          onClick={onEdit}
          data-testid="pet-photo"
        >
          {photoUrl ? (
            <img src={photoUrl} alt={pet.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="text-[#C7C7CC] dark:text-[#48484A]" size={32} />
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0 pt-1">
          <h1 className="text-2xl font-bold text-[#1D1D1F] dark:text-white tracking-tight mb-1">
            {pet.name}
          </h1>
          
          <p className="text-base text-[#8E8E93] mb-3">
            {formatBreed(pet.breed) || speciesLabels[pet.species] || pet.species}
          </p>
          
          {/* Quick Info Pills */}
          <div className="flex flex-wrap gap-2">
            {pet.sex && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F5F7] dark:bg-[#2C2C2E] text-xs font-medium text-[#1D1D1F] dark:text-white">
                <Heart size={12} className="text-pink-500" />
                {pet.sex === 'M' ? 'Maschio' : 'Femmina'}
              </span>
            )}
            {pet.birth_date && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F5F7] dark:bg-[#2C2C2E] text-xs font-medium text-[#1D1D1F] dark:text-white">
                <Calendar size={12} className="text-blue-500" />
                {new Date(pet.birth_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Microchip & Passport Row */}
      <div className="mt-5 flex gap-3">
        {pet.microchip && (
          <div className="flex-1 bg-[#F5F5F7] dark:bg-[#2C2C2E] rounded-2xl px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <Hash size={14} className="text-[#8E8E93]" />
              <span className="text-xs text-[#8E8E93]">Microchip</span>
            </div>
            <p className="font-mono text-sm font-semibold text-[#1D1D1F] dark:text-white tracking-wider">
              {pet.microchip}
            </p>
          </div>
        )}
        
        {pet.eu_passport_number && (
          <div className="flex-1 bg-[#F5F5F7] dark:bg-[#2C2C2E] rounded-2xl px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={14} className="text-[#8E8E93]" />
              <span className="text-xs text-[#8E8E93]">Passaporto UE</span>
            </div>
            <p className="font-mono text-sm font-semibold text-[#1D1D1F] dark:text-white tracking-wider">
              {pet.eu_passport_number}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Standalone mini card for desktop sidebar
export function PetIdentityCard({ pet, onEdit }) {
  if (!pet) return null;
  
  return (
    <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl p-6 shadow-sm border border-black/5 dark:border-white/10">
      <div className="flex gap-4 mb-4">
        <div 
          className="w-20 h-24 rounded-2xl overflow-hidden bg-[#F5F5F7] dark:bg-[#2C2C2E] cursor-pointer"
          onClick={onEdit}
        >
          {pet.photo_url ? (
            <img src={pet.photo_url} alt={pet.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="text-[#C7C7CC]" size={28} />
            </div>
          )}
        </div>
        
        <div className="flex-1 space-y-1.5">
          {pet.microchip && (
            <div className="flex items-center gap-2">
              <Hash size={12} className="text-[#8E8E93]" />
              <span className="font-mono text-xs text-[#1D1D1F] dark:text-white">{pet.microchip}</span>
            </div>
          )}
          {pet.birth_date && (
            <div className="flex items-center gap-2">
              <Calendar size={12} className="text-[#8E8E93]" />
              <span className="text-xs text-[#1D1D1F] dark:text-white">
                {new Date(pet.birth_date).toLocaleDateString('it-IT')}
              </span>
            </div>
          )}
          {pet.sex && (
            <div className="flex items-center gap-2">
              <Heart size={12} className="text-[#8E8E93]" />
              <span className="text-xs text-[#1D1D1F] dark:text-white">
                {pet.sex === 'M' ? 'Maschio' : 'Femmina'}
              </span>
            </div>
          )}
          {pet.weight && (
            <div className="flex items-center gap-2">
              <span className="text-[#8E8E93] text-xs">⚖️</span>
              <span className="text-xs text-[#1D1D1F] dark:text-white">{pet.weight} kg</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-[#F5F5F7] dark:bg-[#2C2C2E] rounded-xl p-3">
          <p className="text-xs text-[#8E8E93]">Colore</p>
          <p className="font-medium text-sm text-[#1D1D1F] dark:text-white">{pet.color || "—"}</p>
        </div>
        <div className="bg-[#F5F5F7] dark:bg-[#2C2C2E] rounded-xl p-3">
          <p className="text-xs text-[#8E8E93]">Razza</p>
          <p className="font-medium text-sm text-[#1D1D1F] dark:text-white">{formatBreed(pet.breed) || "—"}</p>
        </div>
      </div>
      
      <Button 
        variant="outline" 
        onClick={onEdit} 
        className="w-full rounded-xl border-[#E5E5EA] dark:border-[#3A3A3C]"
        data-testid="edit-profile-btn"
      >
        <Edit size={16} className="mr-2" /> Modifica profilo
      </Button>
    </div>
  );
}
